import React, { useEffect, useState, useRef, memo, useCallback, useContext } from 'react';
import Konva from 'konva';

import { ScaleContext } from '../../context/ScaleContext';

import "./Canvas.css";
import { TKModelContext } from '../../context/TKModelContext';

const MIDDLE_HEIGHT_OPERATION = 50;
const DISTANCE_BETWEEN_OPERATIONS_X = 450;
const DISTANCE_BETWEEN_OPERATIONS_Y = 300;
const DISTANCE_UNTIL_NOMENCLATURE = DISTANCE_BETWEEN_OPERATIONS_X / 2;
const DISTANCE_BETWEEN_NOMENCLATURES_Y = 150;

const Canvas = () => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const { scale, isCentered } = useContext(ScaleContext);
  const { selectOperation, tkModel, selectNomenclature, selectParentNomenclatureOperation } = useContext(TKModelContext);

  const canvasRef = useRef(null);
  const layerRef = useRef(null);
  const stageRef = useRef(null);

  const nomenclatureShapes = useRef({});

  const moveStage = useCallback((location) => {
    const { x, y } = location;
    let tween;

    tween = new Konva.Tween({
      duration: 0.35,
      easing: Konva.Easings.EaseInOut,
      node: layerRef.current,
      scaleX: scale,
      scaleY: scale,
      x,
      y,
    })

    tween.play()
    layerRef.current.batchDraw()
  }, [scale])

  function paintAllStrokesBlack(layer) {
    function traverseChildren(node) {
      if (node.hasStroke && node.hasStroke()) {
        node.stroke('black');
      }

      if (node.getChildren && typeof node.getChildren === 'function') {
        node.getChildren().forEach(function (child) {
          traverseChildren(child);
        });
      }
    }

    traverseChildren(layer);

    layer.batchDraw();
  }

  const drawOperationBlock = useCallback((operationBlock, x, y) => {
    const group = new Konva.Group({ id: operationBlock.id, x, y });

    const timeRect = new Konva.Rect({
      x: 25,
      y: 0,
      width: 100,
      height: 20,
      fill: 'white',
      stroke: 'black',
      strokeWidth: 2,
    });
    const timeText = new Konva.Text({
      x: 25,
      y: 0,
      text: operationBlock.time,
      fontSize: 14,
      align: 'center',
      verticalAlign: 'middle',
      width: 100,
      height: 20,
    });

    group.add(timeRect);
    group.add(timeText);

    const rect = new Konva.Rect({
      y: 20,
      width: 150,
      height: 100,
      fill: 'lightgray',
      stroke: 'black',
      strokeWidth: 2,
    });
    const text = new Konva.Text({
      y: 20,
      text: operationBlock.name,
      fontSize: 16,
      padding: 10,
      align: 'center',
      verticalAlign: 'middle',
      width: 150,
      height: 100,
    });

    group.add(rect);
    group.add(text);

    operationBlock.additionalInfo.forEach((info, index) => {
      const infoRect = new Konva.Rect({
        x: 0,
        y: 120 + index * 50,
        width: 150,
        height: 50,
        fill: 'white',
        stroke: 'black',
        strokeWidth: 2,
      });
      const infoText = new Konva.Text({
        x: 0,
        y: 120 + index * 50,
        text: info,
        fontSize: 14,
        align: 'center',
        verticalAlign: 'middle',
        width: 150,
        height: 50,
      });

      group.add(infoRect);
      group.add(infoText);
    });

    group.on('click', function () {
      selectNomenclature(null);
      let rects = group.find('Rect');

      paintAllStrokesBlack(layerRef.current);

      selectOperation(group.id());

      rects.forEach(function (rect) {
        rect.stroke('red');
      });
      layerRef.current.draw();
    });

    layerRef.current.add(group);

    return { group, rect: { x, y, width: 150, height: 120 + operationBlock.additionalInfo.length * 50 } };
  }, [selectNomenclature, selectOperation]);

  const determineNomenclatureColor = useCallback((nomenclature, isOutput) => {
    // Проверяем, является ли номенклатура входной и не обработанной ранее
    const initialInputNomenclatures = new Set();
    if (!isOutput) {
      const isFirstInput = tkModel.operationBlocks.some(operationBlock => {
        return operationBlock.incomingNomenclature.some(n => n.id === nomenclature.id)
      }
      );

      if (isFirstInput && !initialInputNomenclatures.has(nomenclature.id)) {
        initialInputNomenclatures.add(nomenclature.id);
        return '#FFDAB9'; // Персиковый цвет для начальных номенклатур
      }
    }

    // Проверка для финальной выходной номенклатуры
    if (nomenclature.nextItems.length === 0 &&
      tkModel.operationBlocks[tkModel.operationBlocks.length - 1].outgoingNomenclature.some(n => n.id === nomenclature.id)) {
      return 'lightgreen';
    }

    // Проверка на выходную номенклатуру, которая не используется как входная
    if (isOutput) {
      const isUsedAsInput = tkModel.operationBlocks.some(operationBlock =>
        operationBlock.incomingNomenclature.some(n => n.id === nomenclature.id)
      );
      if (!isUsedAsInput) {
        return 'red';
      }
    }

    return 'white';
  }, [tkModel.operationBlocks]);


  const updateOrCreateNomenclature = useCallback((nomenclature, x, y, parentOperationId) => {
    let group = nomenclatureShapes.current[nomenclature.id];

    if (!group) {
      group = new Konva.Group({ id: nomenclature.id, x, y });

      const fill = determineNomenclatureColor(nomenclature, false);

      const path = new Konva.Shape({
        sceneFunc: (context, shape) => {
          context.beginPath();
          context.moveTo(0, 25);
          context.lineTo(30, 0);
          context.lineTo(130, 0);
          context.quadraticCurveTo(130, 25, 130, 50);
          context.lineTo(30, 50);
          context.closePath();
          context.fillStrokeShape(shape);
        },
        fill: fill,
        stroke: 'black',
        strokeWidth: 2,
        width: 100,
        height: 50,
      });

      const text = new Konva.Text({
        text: nomenclature.name,
        fontSize: 11,
        align: 'center',
        verticalAlign: 'middle',
        width: 70,
        height: 50,
      });

      group.add(path);
      group.add(text);

      text.offsetX(text.width() / 4);
      text.offsetY(text.height() / 2);
      text.x(path.width() / 2);
      text.y(path.height() / 2);

      layerRef.current.add(group);
      nomenclatureShapes.current[nomenclature.id] = group;
    } else {
      group.x(x);
      group.y(y);

      const text = group.findOne('Text');
      text.text(nomenclature.name);

      const fill = determineNomenclatureColor(nomenclature, false);
      const path = group.findOne('Shape');
      path.fill(fill);
    }

    group.on('click', function () {
      if (parentOperationId) {
        let shape = group.findOne('Shape');
        selectNomenclature(group.id());
        selectParentNomenclatureOperation(parentOperationId)

        paintAllStrokesBlack(layerRef.current);

        shape.stroke('blue');

        layerRef.current.draw();
      }
    });

    return { group, rect: { x, y, width: 100, height: 50 } };
  }, [determineNomenclatureColor, selectNomenclature, selectParentNomenclatureOperation]);

  const drawArrow = (startRect, endRect, quantity, isOutput = false, isReusingNomenclatureBetweenFarOperations) => {
    const startX = startRect.x + startRect.width;
    const startY = startRect.y + startRect.height / 2;
    const endX = endRect.x;
    const endY = endRect.y + endRect.height / 2;

    const middleX = (startX + endX) / 2;

    // Define the points for the arrow
    const points = [
      startX, startY,
      middleX, startY,
      middleX, endY,
      endX, endY
    ];

    // Create the arrow with the defined points
    const arrow = new Konva.Arrow({
      points: points,
      pointerLength: 10,
      pointerWidth: 10,
      fill: 'black',
      stroke: 'black',
      strokeWidth: 2,
    });

    layerRef.current.add(arrow);
  };



  const drawQuantityRect = (startRect, endRect, quantity, isReusingNomenclatureBetweenFarOperations) => {
    const startX = startRect.x + startRect.width;
    const startY = startRect.y + startRect.height / 2;
    const endX = endRect.x;

    const middleX = (startX + endX) / 2;

    const quantityRect = new Konva.Rect({
      x: middleX - 20,
      y: startY - 15,
      width: 40,
      height: 30,
      fill: 'white',
      stroke: 'black',
      strokeWidth: 2,
      cornerRadius: 5,
    });

    const quantityText = new Konva.Text({
      x: middleX - 20,
      y: startY - 10,
      text: `${quantity} шт`,
      fontSize: 14,
      align: 'center',
      verticalAlign: 'middle',
      width: 40,
      height: 20,
    });

    layerRef.current.add(quantityRect);
    layerRef.current.add(quantityText);
  };

  const handleMouseDown = (event) => {
    const shiftX = event.clientX - layerRef.current.x();
    const shiftY = event.clientY - layerRef.current.y();

    const moveAt = (pageX, pageY) => {
      const newX = pageX - shiftX;
      const newY = pageY - shiftY;

      layerRef.current.x(newX);
      layerRef.current.y(newY);
      layerRef.current.batchDraw();
    };

    const onMouseMove = (event) => {
      moveAt(event.pageX, event.pageY);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.scale({ x: scale, y: scale });
      layerRef.current.batchDraw();
    }
  }, [scale]);

  useEffect(() => {
    const updateDimensions = () => {
      if (canvasRef.current) {
        setDimensions({
          width: canvasRef.current.clientWidth,
          height: canvasRef.current.clientHeight,
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (canvasRef.current) {
      resizeObserver.observe(canvasRef.current);
    }

    return () => {
      if (canvasRef.current) {
        resizeObserver.unobserve(canvasRef.current);
      }
    };
  }, []);

  useEffect(() => {
    stageRef.current = new Konva.Stage({
      container: canvasRef.current,
      width: dimensions.width,
      height: dimensions.height,
    });

    layerRef.current = new Konva.Layer();
    stageRef.current.add(layerRef.current);

    const operations = tkModel.getOperations();
    const finalOperation = tkModel.getFinalOperation();

    if (finalOperation) {
      const operationsWithXCoordinate = tkModel.addXPositionToOperation(operations, finalOperation);

      tkModel.markHeightInOutgoingNomenclatures(operationsWithXCoordinate);
      tkModel.markHeightIncomingNomenclatures(operationsWithXCoordinate);
      tkModel.markHeightInOperations(operationsWithXCoordinate);

      const operationsRect = [];

      operationsWithXCoordinate.forEach((currentOperationLine, currentOperationLineIndex) => {
        currentOperationLine.forEach((op, opIndex) => {
          const opXIndexWidth = op.xIndex === 0 ? 0 : (op.xIndex) * DISTANCE_BETWEEN_OPERATIONS_X * -1;
          const opYIndexHeight = op.yIndex === 0 ? 0 : (op.yIndex) * DISTANCE_BETWEEN_OPERATIONS_Y;
          const incomingNomenclatures = [];
          const outgoingNomenclatures = [];

          op.incomingNomenclature.forEach(nom => {
            const posX = opXIndexWidth - DISTANCE_UNTIL_NOMENCLATURE;
            const posY = opYIndexHeight + MIDDLE_HEIGHT_OPERATION + (nom.yIndex * DISTANCE_BETWEEN_NOMENCLATURES_Y);

            const incomingNomenclatureBlock = updateOrCreateNomenclature(nom, posX, posY, op.id);
            incomingNomenclatures.push({ rect: incomingNomenclatureBlock.rect, block: nom });
          })

          op.outgoingNomenclature.forEach(nom => {
            if (nom.isWasteNomenclature || op.id === op.finalOperationId) {
              const posX = opXIndexWidth + DISTANCE_UNTIL_NOMENCLATURE;
              const posY = opYIndexHeight + MIDDLE_HEIGHT_OPERATION + (nom.yIndex * DISTANCE_BETWEEN_NOMENCLATURES_Y) + (nom.isWasteNomenclature ? DISTANCE_BETWEEN_NOMENCLATURES_Y : 0);
              const outgoingNomenclatureBlock = updateOrCreateNomenclature(nom, posX, posY);
              outgoingNomenclatures.push({ rect: outgoingNomenclatureBlock.rect, block: nom });
            }
          })

          const maxNomenclaturesLength = op.incomingNomenclature.length > op.outgoingNomenclature.length ? op.incomingNomenclature.length : op.outgoingNomenclature.length;
          const MIDDLE_HEIGHT_OF_NOMENCLATURES = ((maxNomenclaturesLength / 2) * (DISTANCE_BETWEEN_NOMENCLATURES_Y) - 50);

          const operationBlock = drawOperationBlock(op, opXIndexWidth, opYIndexHeight + MIDDLE_HEIGHT_OF_NOMENCLATURES);

          op.outgoingNomenclature.forEach(nom => {
            if (nom.isWasteNomenclature || op.id === op.finalOperationId) {
              const posX = opXIndexWidth + DISTANCE_UNTIL_NOMENCLATURE;
              const posY = opYIndexHeight + MIDDLE_HEIGHT_OPERATION + (nom.yIndex * DISTANCE_BETWEEN_NOMENCLATURES_Y) + (nom.isWasteNomenclature ? DISTANCE_BETWEEN_NOMENCLATURES_Y : 0);
              updateOrCreateNomenclature(nom, posX, posY);
            }
          })

          incomingNomenclatures.forEach(nom => {
            drawArrow(nom.rect, operationBlock.rect);
            drawQuantityRect(nom.rect, operationBlock.rect, nom.block.quantity);
          });

          outgoingNomenclatures.forEach(nom => {
            if (nom.block.isWasteNomenclature || op.id === op.finalOperationId) {
              drawArrow(operationBlock.rect, nom.rect);
              drawQuantityRect(operationBlock.rect, nom.rect, nom.block.quantity);
            }
          });

          operationsRect.push({ operationRect: operationBlock.rect, incomingNomenclatures, outgoingNomenclatures, operation: op });
        });
      });

      operationsRect.forEach(({ operationRect, incomingNomenclatures, outgoingNomenclatures, operation }, operationIndex) => {
        incomingNomenclatures.forEach(incN => {
          let nextIncN;
          operations.forEach(prevOp => {
            const prevOutNomenclature = prevOp.outgoingNomenclature.find(({ id }) => id === incN.block.id);

            if (prevOutNomenclature) {
              nextIncN = incN;
              const prevOperation = operationsRect.find(({ operation }) => operation.id === prevOp.id);


              drawArrow(prevOperation.operationRect, nextIncN.rect);
              drawQuantityRect(prevOperation.operationRect, nextIncN.rect, prevOutNomenclature.quantity);
            }
          });
        })
      })
    }


    layerRef.current.draw();

    return () => {
      layerRef.current.destroyChildren();
      nomenclatureShapes.current = {};
    };
  }, [dimensions.width, dimensions.height, tkModel, drawOperationBlock, updateOrCreateNomenclature]);

  useEffect(() => {
    if (isCentered) {
      moveStage({ x: layerRef.current.width() / 2 , y: layerRef.current.height() / 2 })
    }
  }, [dimensions, isCentered, moveStage])

  return (
    <div
      ref={canvasRef}
      onMouseDown={handleMouseDown}
      style={{
        width: '100%',
        height: '100%',
      }}
    />
  );
};

export default memo(Canvas);